import React, { Component } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withRouter, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Paginator from '../../../components/Paginator';
import { sortUrl, parse, toSortOrder } from '../../../utils/UrlUtil';
import TableHeader from './JobProfileSurveyListHeader';
import { matchShape, intlShape } from '../../../components/prop-types';

const messages = defineMessages({
  new: { id: 'candidate.status.NEW', defaultMessage: 'NEW' },
  invited: { id: 'candidate.status.INVITED', defaultMessage: 'INVITED' },
  started: { id: 'candidate.status.STARTED', defaultMessage: 'IN PROGRESS' },
  declined: { id: 'candidate.status.DECLINED', defaultMessage: 'DECLINED' },
  expired: { id: 'candidate.status.EXPIRED', defaultMessage: 'EXPIRED' },
  submitted: { id: 'candidate.status.SUBMITTED', defaultMessage: 'ASSESSED' },
  fileName: { id: 'attrs.fileName', defaultMessage: 'File Name' },

  name: { id: 'attrs.name', defaultMessage: 'Name' },
  template: { id: 'attrs.assessmentTemplate', defaultMessage: 'Assessment Template' },
  language: { id: 'attrs.language', defaultMessage: 'Language' },
  status: { id: 'attrs.status', defaultMessage: 'Status' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  updated: { id: 'attrs.updated', defaultMessage: 'Updated' },
});

const getItemStatusProperties = (status, formatMessage) => {
  switch (status) {
    case 'NEW': {
      return {
        styleClass: 'filter is-active',
        text: formatMessage(messages.new),
      };
    }
    case 'INVITED': {
      return {
        styleClass: 'filter filter--info is-active',
        text: formatMessage(messages.invited),
      };
    }
    case 'STARTED': {
      return {
        styleClass: 'filter filter--warning is-active',
        text: formatMessage(messages.started),
      };
    }
    case 'DECLINED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.declined),
      };
    }
    case 'EXPIRED': {
      return {
        styleClass: 'filter filter--error is-active',
        text: formatMessage(messages.expired),
      };
    }
    case 'SUBMITTED': {
      return {
        styleClass: 'filter filter--success is-active',
        text: formatMessage(messages.submitted),
      };
    }
    default: {
      return {};
    }
  }
};

const statusTemplate = (rowData, { formatMessage }) => {
  const statusProp = getItemStatusProperties(rowData.status, formatMessage);
  const { styleClass, text } = statusProp;
  if (statusProp) {
    return <span className={styleClass}>{text}</span>;
  }

  return null;
};

const nameTemplate = (rowData, {
  currentAccount, keyField, displayField, match: { params },
}) => {
  const { jobProfileId } = params;
  const detailUrl = `/accounts/${currentAccount}/job-profiles/${jobProfileId}/surveys/${rowData[keyField]}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData[displayField]}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

class JobProfileSurveyList extends Component {
  constructor(props) {
    super(props);
    this.onSortChange = this.onSortChange.bind(this);
  }

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  render() {
    const {
      connection: {
        edges, totalCount, indexFrom, indexTo, pageInfo,
      },
      intl: { formatMessage, formatDate },
      currentAccount,
      loading,
      location,
      match,
    } = this.props;

    // data list
    const listRows = edges.map(({ node }) => ({
      ...node,
      respondentName: node.respondent ? `${node.respondent.firstName} ${node.respondent.lastName}` : '',
    }));

    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    // header
    const nameHeader = formatMessage(messages.fileName);
    const statusHeader = formatMessage(messages.status);
    const createdHeader = formatMessage(messages.created);
    const updatedHeader = formatMessage(messages.updated);

    // footer
    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;
    return (
      <DataTable
        value={listRows}
        header={<TableHeader />}
        footer={footer}
        responsive
        sortField={sortBy}
        sortOrder={toSortOrder(sortDir)}
        onSort={this.onSortChange}
        lazy
        loading={loading}
      >
        <Column
          field="respondentName"
          header={nameHeader}
          body={nameTemplate}
          keyField="id"
          match={match}
          displayField="respondentName"
          currentAccount={currentAccount}
          sortable
        />
        <Column field="status" header={statusHeader} body={statusTemplate} formatMessage={formatMessage} />
        <Column field="createdOn" header={createdHeader} body={timeFieldTemplate} formatDate={formatDate} sortable />
        <Column field="updatedOn" header={updatedHeader} body={timeFieldTemplate} formatDate={formatDate} sortable />
      </DataTable>
    );
  }
}

JobProfileSurveyList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  match: matchShape.isRequired,
};

export default withRouter(injectIntl(JobProfileSurveyList));
